/**
 * Stammdaten eines geöffneten Genehmigungsantrags – dieselben Abschnitte wie beim Anlegen
 * (`AntragFelder`), ergänzt um das, was erst im Verfahren entsteht.
 *
 * Gespeichert wird ohne Schaltfläche: Textfelder beim Verlassen, die Mehrfachauswahl beim
 * Schließen ihres Menüs, einwertige Auswahlfelder sofort mit der Änderung, Daten beim Verlassen
 * bzw. mit der Auswahl im Kalender (docs/adr/004_autosave_dirty_check.md, Blur-Pattern). Ob sich
 * der Wert überhaupt geändert hat, prüft der Hook – hier steht nur fachliche Validierung.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { OrgUser } from "../../../../services/api/users";
import {
  AnlageOption,
  LookupOption,
  ProcedureTypeOption,
  PermitProcedure,
  PermitProcedureUpdate,
  ProcedureAction,
  StandortmerkmalOption,
  UvpStatusOption,
  WindenergiegebietOption,
} from "../../../../types";
import AntragKonferenz from "./AntragKonferenz";
import AntragFelder, {
  AntragFeld,
  AntragFeldHandler,
  AntragFeldWerte,
} from "./AntragFelder";

interface AntragStammdatenProps {
  procedure: PermitProcedure;
  procedureTypeOptions: ProcedureTypeOption[];
  uvpStatusOptions: UvpStatusOption[];
  vorhabenbestandteilOptions: LookupOption[];
  /** Die Anlagen des Projekts – Auswahl für den Antragsgegenstand */
  anlageOptionen: AnlageOption[];
  windenergiegebietOptions: WindenergiegebietOption[];
  standortmerkmalOptions: StandortmerkmalOption[];
  /** Benutzer des Mandanten – Auswahl für die Bearbeitung */
  orgUsers: OrgUser[];
  canEdit: boolean;
  /** Speichert die geänderten Felder als Teil-Update */
  onFieldChange: (data: PermitProcedureUpdate) => void;
  /** Öffnet den Dialog zu einer Aktion am Antrag – „Antragskonferenz erfassen“ */
  onAction: (action: ProcedureAction) => void;
}

/** Der gespeicherte Stand des Antrags in der Form der Felder. */
const werteAusAntrag = (procedure: PermitProcedure): AntragFeldWerte => ({
  procedureTypeId: procedure.procedure_type_id ?? "",
  bezeichnung: procedure.bezeichnung ?? "",
  bearbeitungUserId: procedure.bearbeitung_user_id ?? "",
  bezugsgenehmigungId: procedure.bezugsgenehmigung_id ?? "",
  zusatzantragBemerkung: procedure.zusatzantrag_bemerkung ?? "",
  beschreibung: procedure.beschreibung ?? "",
  anlageIds: procedure.anlage_ids ?? [],
  vorhabenbestandteilIds: (procedure.vorhabenbestandteile ?? []).map(
    (bestandteil) => bestandteil.id,
  ),
  uvpStatusId: procedure.uvp_status_id ?? "",
  windenergiegebietId: procedure.windenergiegebiet_id ?? "",
  standortmerkmalIds: (procedure.standortmerkmale ?? []).map(
    (merkmal) => merkmal.id,
  ),
  eingereichtAm: procedure.eingereicht_am ?? "",
  vollstaendigSeit: procedure.vollstaendig_seit ?? "",
  genehmigungGeplantAm: procedure.genehmigung_geplant_am ?? "",
  conferencePlannedOn: procedure.conference_planned_on ?? "",
  bescheidVom: procedure.bescheid_vom ?? "",
  aktenzeichen: procedure.aktenzeichen ?? "",
});

const leerAlsNull = (wert: string) => wert || null;

/**
 * Das Teil-Update zu einem abgeschlossenen Feld, oder eine Fehlermeldung, wenn ein Pflichtfeld
 * leer bleiben soll. Ein leeres Pflichtfeld wird nicht gespeichert: der letzte gültige Stand
 * bleibt am Antrag stehen, das Feld zeigt die Meldung.
 */
const updateFuer = <F extends AntragFeld>(
  feld: F,
  wert: AntragFeldWerte[F],
): PermitProcedureUpdate | { fehler: string } => {
  const text = wert as string;
  const liste = wert as string[];
  switch (feld) {
    case "procedureTypeId":
      return { procedure_type_id: text };
    case "bezeichnung":
      return text.trim()
        ? { bezeichnung: text.trim() }
        : { fehler: "Bezeichnung ist erforderlich" };
    case "bearbeitungUserId":
      // Pflicht, weil die Wiedervorlagen dieses Antrags daran hängen.
      return text
        ? { bearbeitung_user_id: text }
        : { fehler: "Bearbeitung ist erforderlich" };
    case "bezugsgenehmigungId":
      return { bezugsgenehmigung_id: leerAlsNull(text) };
    case "zusatzantragBemerkung":
      return { zusatzantrag_bemerkung: leerAlsNull(text) };
    case "beschreibung":
      return { beschreibung: leerAlsNull(text) };
    case "anlageIds":
      return { anlage_ids: liste };
    case "vorhabenbestandteilIds":
      return liste.length > 0
        ? { vorhabenbestandteil_ids: liste }
        : { fehler: "Vorhabenumfang ist erforderlich" };
    case "uvpStatusId":
      return { uvp_status_id: text };
    case "windenergiegebietId":
      return { windenergiegebiet_id: text };
    case "standortmerkmalIds":
      return { standortmerkmal_ids: liste };
    case "eingereichtAm":
      return { eingereicht_am: leerAlsNull(text) };
    case "vollstaendigSeit":
      return { vollstaendig_seit: leerAlsNull(text) };
    case "genehmigungGeplantAm":
      return { genehmigung_geplant_am: leerAlsNull(text) };
    case "conferencePlannedOn":
      return { conference_planned_on: leerAlsNull(text) };
    case "bescheidVom":
      return { bescheid_vom: leerAlsNull(text) };
    case "aktenzeichen":
      return { aktenzeichen: leerAlsNull(text) };
  }
  return {};
};

const AntragStammdaten: React.FC<AntragStammdatenProps> = ({
  procedure,
  procedureTypeOptions,
  uvpStatusOptions,
  vorhabenbestandteilOptions,
  anlageOptionen,
  windenergiegebietOptions,
  standortmerkmalOptions,
  orgUsers,
  canEdit,
  onFieldChange,
  onAction,
}) => {
  const vomServer = useMemo(() => werteAusAntrag(procedure), [procedure]);

  // Was getippt bzw. angehakt, aber noch nicht gespeichert ist – je Feld. Tippen soll keinen
  // Request auslösen, erst der Abschluss der Eingabe speichert.
  const [entwurf, setEntwurf] = useState<Partial<AntragFeldWerte>>({});

  // Die Antwort des Servers ersetzt den Entwurf nur für die Felder, die sich dort geändert
  // haben – so setzt das Speichern eines Feldes nicht den Text zurück, den der Nutzer gerade in
  // ein anderes tippt. Verglichen wird über den Inhalt: Der Server liefert bei jedem Speichern
  // neue Listen, und auf die Liste selbst zu hören setzte eine offene Mehrfachauswahl zurück.
  const bisherVomServer = useRef(vomServer);
  useEffect(() => {
    const bisher = bisherVomServer.current;
    bisherVomServer.current = vomServer;
    const geaendert = (Object.keys(vomServer) as AntragFeld[]).filter(
      (feld) =>
        JSON.stringify(vomServer[feld]) !== JSON.stringify(bisher[feld]),
    );
    if (geaendert.length === 0) return;
    setEntwurf((vorher) => {
      const naechster = { ...vorher };
      geaendert.forEach((feld) => delete naechster[feld]);
      return naechster;
    });
  }, [vomServer]);

  const werte: AntragFeldWerte = { ...vomServer, ...entwurf };

  const [formErrors, setFormErrors] = useState<
    Partial<Record<AntragFeld, string>>
  >({});

  const aendere: AntragFeldHandler = (feld, wert) =>
    setEntwurf((vorher) => ({ ...vorher, [feld]: wert }));

  const speichere: AntragFeldHandler = (feld, wert) => {
    const ergebnis = updateFuer(feld, wert);
    if ("fehler" in ergebnis) {
      setFormErrors((bisher) => ({ ...bisher, [feld]: ergebnis.fehler }));
      return;
    }
    setFormErrors((bisher) => {
      const { [feld]: _behoben, ...rest } = bisher;
      return rest;
    });
    onFieldChange(ergebnis);
  };

  return (
    <AntragFelder
      variante="stammdaten"
      werte={werte}
      onChange={aendere}
      onCommit={speichere}
      fehler={formErrors}
      disabled={!canEdit}
      procedureTypeOptions={procedureTypeOptions}
      uvpStatusOptions={uvpStatusOptions}
      vorhabenbestandteilOptions={vorhabenbestandteilOptions}
      anlageOptionen={anlageOptionen}
      windenergiegebietOptions={windenergiegebietOptions}
      standortmerkmalOptions={standortmerkmalOptions}
      orgUsers={orgUsers}
      projectId={procedure.project_id}
      eigeneId={procedure.id}
      konferenz={
        <AntragKonferenz
          procedure={procedure}
          canEdit={canEdit}
          onFieldChange={onFieldChange}
          onAction={onAction}
        />
      }
    />
  );
};

export default AntragStammdaten;
